import { LoginCredentials, LoginSecurityContext } from '../api/agwa-api-model';
import { dateFromServer } from '../utility/dates';
import { UserAccessManager } from '../utility/user-access-manager';
import { UserAccessManagerImpl } from '../utility/user-access-manager-impl';

// Need to re-import LoginCredentials for the helper function

export type AuthProviderType = 'internal' | 'imis';

/**
 * Base interface containing fields specific to UserProfileData
 * that are not present in LoginCredentials
 */
export interface UserProfileSpecificData {
  authProvider: AuthProviderType;
  profileImageBase64?: string;
  securityContext?: LoginSecurityContext;
}

/**
 * User profile data combining LoginCredentials properties
 * and UserProfile specific properties
 */
export interface UserProfileData extends UserProfileSpecificData {
  username: string;
  displayName: string;
  emailAddress?: string;
  token?: string;
  expiresUtc?: string;
  isTwoFactorEnabled?: boolean;
  claims: Readonly<Record<string, string>>;
  userId?: string; // Add userId field to store the user's ID
  loginGroupId?: string; // Direct group ID from credentials
  loginGroupName?: string; // Direct group name from credentials
}

// Utility type for creating user profiles from different auth providers
export type UserProfileFromProvider<T> = Omit<
  UserProfileData,
  'authProvider'
> & {
  authProvider: T;
};

export class UserProfile implements UserProfileData {
  public access: UserAccessManager;
  needsRefresh() {
    if (!this.expiresUtc) {
      return false;
    }
    const expirationDate = dateFromServer(this.expiresUtc);
    const currentDate = new Date();
    const fourHoursFromNow = new Date(
      currentDate.getTime() + 4 * 60 * 60 * 1000
    );

    return expirationDate <= fourHoursFromNow;
  }

  expiresAtMilliseconds() {
    if (!this.expiresUtc) {
      return 0;
    }
    const expirationDate = dateFromServer(this.expiresUtc);
    const currentDate = new Date();
    return expirationDate.getTime() - currentDate.getTime();
  }

  private readonly _data: UserProfileData;

  get data(): UserProfileData {
    return this._data;
  }

  constructor(data: UserProfileData) {
    this._data = data;
    this.access = new UserAccessManagerImpl(
      data.claims,
      data.securityContext,
      data.loginGroupId,
      data.loginGroupName
    );
  }
  get claims(): Readonly<Record<string, string>> {
    return this._data.claims;
  }

  get username(): string {
    return this._data.username;
  }
  get isTwoFactorEnabled(): boolean {
    return this._data.isTwoFactorEnabled ?? false;
  }
  set isTwoFactorEnabled(value: boolean) {
    this._data.isTwoFactorEnabled = value;
  }

  get isInternal(): boolean {
    return this._data.authProvider === 'internal';
  }
  get displayName(): string {
    return this._data.displayName;
  }

  get emailAddress(): string | undefined {
    return this._data.emailAddress;
  }

  get authProvider(): AuthProviderType {
    return this._data.authProvider;
  }

  get profileImageBase64(): string | undefined {
    return this._data.profileImageBase64;
  }

  get token(): string | undefined {
    return this._data.token;
  }

  get securityContext(): LoginSecurityContext | undefined {
    return this._data.securityContext;
  }

  get expiresUtc(): string | undefined {
    return this._data.expiresUtc;
  }

  get isLoggedIn(): boolean {
    return (
      //  this.token !== undefined &&
      this.expiresUtc !== undefined && new Date(this.expiresUtc) > new Date()
    );
  }

  hasValidAccessToken(): boolean {
    return (
      //  !!this.token &&
      !!this.expiresUtc && dateFromServer(this.expiresUtc) > new Date()
    );
  }

  toJSON(): UserProfileData {
    return this._data;
  }

  static fromJSON(json: UserProfileData): UserProfile {
    return new UserProfile(json);
  }

  async performLogoutOperations() {}

  // Add userId getter
  get userId(): string | undefined {
    // First try to get the userId directly if it exists
    if (this._data.userId) {
      return this._data.userId;
    }

    // Otherwise try to extract it from claims
    if (this._data.claims) {
      // Check common claim keys that might contain user ID
      return (
        this._data.claims['sub'] ||
        this._data.claims['nameidentifier'] ||
        this._data.claims['userId'] ||
        this._data.claims['id']
      );
    }

    return undefined;
  }
}

/**
 * Helper function to merge UserProfileData with LoginCredentials
 * This preserves all user profile data but overwrites with values from credentials
 * @param profile The user profile data
 * @param credentials The login credentials to merge
 * @returns A new UserProfileData object with merged properties
 */
export function mergeProfileWithCredentials(
  profile: UserProfileSpecificData,
  credentials: LoginCredentials
): UserProfileData {
  return {
    ...profile,
    // Override with credentials properties
    token: credentials.token,
    expiresUtc: credentials.expiresUtc,
    // Take additional properties from credentials that weren't in the original type
    username: credentials.username,
    displayName: credentials.displayName,
    emailAddress: credentials.emailAddress || undefined,
    claims: {}, // Claims are deprecated - all info now in concrete properties
    isTwoFactorEnabled: credentials.isTwoFactorEnabled,
    // Direct group properties from credentials
    loginGroupId: credentials.loginGroupId || undefined,
    loginGroupName: credentials.loginGroupName || undefined,
    // Extract userId from credentials
    userId: credentials.loginUserId,
    securityContext: credentials.securityContext ?? profile.securityContext,
  };
}
